// J-COLLECTION-02: 集金データアクセス層 (J-COLLECTION-01全面置換)
import { supabase } from '../lib/supabase'
// SPEC-COLLECTION-METER-CARRY-COMPENSATION-V1 (D-583): 集金期間中のメーター不連続を足し戻す。
import { sumCompensations } from './meterCarryCompensation'
import { Sentry } from '../lib/sentry'
import { fetchAllRange } from './pagedFetch' // SPEC-...ROWCAP-FIX-01 (D-395) ITEM_3: フィルタ合計の全件取得
import { DFX_ORG_ID, CHANGE_ORG_ID } from '../lib/auth/orgConstants'
import { genCollectionId, boothTotal, compareCollectionBooths, resolveUnitPrice } from '../collection/lib/collectionCalc'
// SPEC-COLLECTION-FLAG-SYNC-01 (D-253): 集金フラグ整合の判定純関数(立てる/落とす・最新集金ガード)。
import { isLatestCollection } from '../collection/lib/collectionFlags'
// D-1062: 店ごとの最新の実行日を出す純関数。
import { latestExecutedAtByStore } from './collectionHistoryFilter'
// D-1017: 稼働ブース = booths.is_active かつ 親機械 is_active(共通の絞り込み)。
import { keepBoothsOfActiveMachines } from './boothScope'

// SPEC-ANOMALY-METER-P1-COLLECTION-AUDIT-01: 集金保存成功後に単価乖離監査RPCを非同期実行。
// fire-and-forget。RPC失敗は集金保存を絶対にブロック/ロールバックしない (Sentry記録のみ)。
// サーバ側集計 (fn_audit_collection_meters) のみ、クライアント集計禁止。
export async function auditCollectionMeters(collectionId) {
  try {
    const { error } = await supabase.rpc('fn_audit_collection_meters', { p_collection_id: collectionId })
    if (error) throw error
  } catch (e) {
    Sentry.captureException(e, { tags: { area: 'collection-meter-audit' }, extra: { collectionId } })
  }
}

// SPEC-COLLECTION-FLAG-SYNC-01 (D-253): 集金確定時に巡回の集金フラグ(meter_readings.is_collected/flagged_at)を整合させる。
//   saveCollection と saveSignedPdf の両方から呼ぶ唯一の実装(インライン重複を持たない=AC1)。
//   ROOT_CAUSE: 旧 auto-flag は saveSignedPdf 内インラインのみで、先方署名が集金入力画面に移った後はデッドコード化していた。
//   処理は2つ:
//     立てる = entry_type=patrol / patrol_date <= collected_at(JST) / 未フラグ(null|false)を is_collected=true・flagged_at=集金日JST00:00。
//     落とす = entry_type=patrol / patrol_date > collected_at(JST) / is_collected=true を false・flagged_at=null。
//       ← DESIGN_KEY_POINT: 集金日より後に残った手動フラグ(唐津 7/23)を落とすのが本体。「立てる」だけでは起算日が狂う。
//       ← ガード: 当該集金が店の最新集金(collected_at が最大と同等以上)のときのみ落とす(古い集金の後入力で新サイクルを消さない)。
//   対象ブースは当該集金の cash_collection_booths のうち in_meter_current!=null の booth_code のみ(store_code 一括禁止=AC2)。
//     理由: 同店でもメーター未入力で集金に含まれないブース(合志 KOS01 等)まで店の集金日で立てると、そのブース自身の集金日を
//     追い越して今回の唐津と同型の事故を自分で作るため。
//   失敗しても集金保存の成功を打ち消さない(warn のみ=AC4)。JST は sv-SE + Asia/Tokyo(toISOString split/slice 禁止=AC5)。
//   更新列は is_collected と flagged_at のみ(FORBIDDEN 遵守)。
export async function syncCollectionFlags(collectionId) {
  if (!collectionId) return
  try {
    const { data: col } = await supabase.from('cash_collections')
      .select('store_code, collected_at').eq('collection_id', collectionId).single()
    if (!col?.store_code || !col?.collected_at) return

    // 対象ブース = 当該集金でメーター入力済(in_meter_current not null)のブースに限定。
    // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
    const { data: cbs } = await supabase.from('cash_collection_booths')
      .select('booth_code, in_meter_current').eq('collection_id', collectionId)
    const targetBooths = (cbs ?? []).filter(b => b.in_meter_current != null).map(b => b.booth_code)
    if (targetBooths.length === 0) return

    const collectedDateJst = new Date(col.collected_at).toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
    const flaggedAt = `${collectedDateJst}T00:00:00+09:00` // 集金日 JST 00:00(toISOString を使わない)

    // 立てる: 集金日以前の未フラグ行にフラグを付ける。
    await supabase.from('meter_readings')
      .update({ is_collected: true, flagged_at: flaggedAt })
      .eq('entry_type', 'patrol')
      .in('booth_code', targetBooths)
      .lte('patrol_date', collectedDateJst)
      .or('is_collected.is.null,is_collected.eq.false')

    // 落とす: 当該集金が店の最新集金のときのみ、集金日より後に残ったフラグを落とす。
    // D-1021: 取消(status='cancelled')の集金は「最新」に数えない。取消した新しい行があると落とすが走らなかった。
    const { data: latest } = await supabase.from('cash_collections')
      .select('collected_at')
      .eq('store_code', col.store_code)
      .eq('status', 'confirmed')
      .order('collected_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (isLatestCollection(col.collected_at, latest?.collected_at)) {
      await supabase.from('meter_readings')
        .update({ is_collected: false, flagged_at: null })
        .eq('entry_type', 'patrol')
        .in('booth_code', targetBooths)
        .gt('patrol_date', collectedDateJst)
        .eq('is_collected', true)
    }
  } catch (e) {
    // フラグ整合の失敗は集金保存の成功を打ち消さない(既存 auto-flag と同思想)。
    console.warn('SPEC-COLLECTION-FLAG-SYNC-01 syncCollectionFlags warn:', e?.message ?? e)
  }
}

// アクティブ店舗 (is_active=true)
export async function getActiveStores() {
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data, error } = await supabase
    .from('stores')
    // D-476: partner_share_pct は集金画面の分配率入力欄の初期値(マスタ値)。null=分配なし(ドンキ系)。
    .select('store_code, store_name, store_name_official, partner_share_pct')
    .eq('is_active', true)
    .order('store_name')
  return { data: data ?? [], error }
}

// 店舗のアクティブブース一覧 + 表示名 + 集金日<=meter_readings 最新プリフィル + 直前confirmed集金からprevプリフィル
// J-COLLECTION-04 fix_1/2/3/4:
//   - rental_code = machine_number ?? '' (NULL=空欄)
//   - booth_name = booth_label ?? `${machine_code末尾}-B{nn}` (例 M04-B02)
//   - in_meter_current_default = patrol_date <= collectedAt の meter_readings最新
//   - in_meter_prev_default    = 当該店舗の直前confirmed cash_collection_booths.in_meter_current
export async function getActiveBoothsForStore(storeCode, collectedAt, prevDate) {
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data: booths, error } = await supabase
    .from('booths')
    .select('booth_code, machine_code, booth_number, booth_label, is_active')
    .eq('store_code', storeCode)
    .eq('is_active', true)
    .order('machine_code')
    .order('booth_code')
  if (error) return { data: null, error }
  if (!booths || booths.length === 0) return { data: [], error: null }

  const boothCodes = booths.map(b => b.booth_code)
  const machineCodes = [...new Set(booths.map(b => b.machine_code))]

  // meter_readings は patrol_date <= collectedAt に限定 (集金日基準)
  let mrQuery = supabase.from('meter_readings')
    .select('booth_code, in_meter, out_meter, patrol_date, created_at')
    .in('booth_code', boothCodes)
    .eq('entry_type', 'patrol')
  if (collectedAt) mrQuery = mrQuery.lte('patrol_date', collectedAt)
  mrQuery = mrQuery.order('patrol_date', { ascending: false }).order('created_at', { ascending: false })

  const [{ data: machines }, { data: readings }, prevInfo] = await Promise.all([
    // SPEC-COLLECTION-CHANGER-EXCLUDE-SSOT-01 (D-203) S1: 両替機判定を機種マスタ(machine_models.type_id)へ寄せる。
    //   machine_models!model_id は left join(!inner 禁止)=model_id null の将来機でもブースが集金一覧から丸ごと消えない。
    //   S2: machines.type_id は当該経路の SELECT から外し一切読まない。
    // SPEC-COLLECTION-SIGN-POS-AND-AMOUNT-TOTAL-01 (D-273) B4: 単価解決用に meter_unit_price / meter_layout を追加取得(読むだけ)。
    // D-1017: 親機械の稼働判定のため is_active を読む(下の keepBoothsOfActiveMachines が使う。表示対象の絞り込みだけ)。
    // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
    supabase.from('machines').select('machine_code, machine_name, machine_number, billing_order, model_id, is_active, machine_models!model_id(type_id, meter_unit_price, meter_layout)').in('machine_code', machineCodes),
    mrQuery,
    getPrevMeterAfterDate(storeCode, boothCodes, prevDate, collectedAt),
  ])
  const mMap = Object.fromEntries((machines ?? []).map(m => [m.machine_code, m]))

  // SPEC-COLLECTION-CHANGER-EXCLUDE-SSOT-01 (D-203) S2/S3/S4/S5: 両替機除外は machine_models.type_id で判定(表示のみ)。
  //   S5: 埋め込みは配列/オブジェクトどちらでも返りうるので先頭を安全に取り出す(unitPriceTotals.mm と同形。ただし
  //   services→clawsupport の依存は方向違反ゆえ import せず collections.js 内に閉じる=S4)。
  //   S3: model_id null / 埋め込み空(=機種解決不可)は両替機と見なさず集金一覧に残す(安全側。巡回の sonota 寄せと同思想)。
  const modelTypeId = m => {
    const mm = Array.isArray(m?.machine_models) ? m.machine_models[0] : m?.machine_models
    return mm?.type_id ?? null
  }
  const changerMcs = new Set((machines ?? []).filter(m => modelTypeId(m) === 'changer').map(m => m.machine_code))

  const latestPerBooth = {}
  for (const r of readings ?? []) {
    if (!latestPerBooth[r.booth_code]) latestPerBooth[r.booth_code] = r
  }

  // D-1017: 撤去済み(machines.is_active=false)の機械のブースは、ブースが is_active のまま残っていても並べない。
  //   ★一覧に出すかどうかだけ。金額・保存の経路は触らない(集金根拠)。機械行が引けないブースは残す(fail-open)。
  const rows = keepBoothsOfActiveMachines(booths, mMap).filter(b => !changerMcs.has(b.machine_code)).map(b => {
    const r = latestPerBooth[b.booth_code]
    const m = mMap[b.machine_code] || {}
    const mcTail = b.machine_code?.split('-').pop() ?? b.machine_code
    return {
      booth_code: b.booth_code,
      machine_code: b.machine_code,
      // fix_1: rental_code = machine_number ?? '' (NULL=空欄、表示はboothで補完)
      rental_code: m.machine_number ?? '',
      machine_name: m.machine_name || b.machine_code || '機械不明',
      // fix_2: booth_name = booth_label(上書き優先) ?? `${machine_code末尾}-B{nn}` 例 M04-B02
      booth_name: b.booth_label
        || (b.booth_number != null ? `${mcTail}-B${String(b.booth_number).padStart(2, '0')}` : b.booth_code),
      booth_number: b.booth_number,
      // SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_1: 並び替え材料を row に載せる(共通比較関数 compareCollectionBooths が読む)。
      billing_order: m.billing_order ?? null,
      // SPEC-COLLECTION-SIGN-POS-AND-AMOUNT-TOTAL-01 (D-273) B4: 単価を解決して row に載せる(店舗計の金額集計が読む)。
      unit_price: resolveUnitPrice(m.machine_models),
      in_meter_current_default: r?.in_meter ?? null,
      out_meter_current_default: r?.out_meter ?? null,
      // fix_3: prev = 当該店舗の直前confirmed cash_collection の in_meter_current
      in_meter_prev_default: prevInfo?.prevMap?.[b.booth_code] ?? null,
      // SPEC-COLLECTION-METER-CARRY-COMPENSATION-V1 (D-583): 集金期間中の一周/交換ぶんの足し戻し量。
      //   ★差の計算にだけ使う。保存する前回IN/今回INは実測値のまま(FORBIDDEN)。
      meter_carry_comp: prevInfo?.compMap?.[b.booth_code] ?? 0,
      // ★旧メーター最終値が分からず補正できなかったブース。黙って一部だけ足さず画面に出す。
      meter_carry_unknown: !!prevInfo?.unknownSet?.[b.booth_code],
      // D-1073 直し: 補正できなかった理由(画面の文言の出し分けだけに使う。計算・保存には使わない)。
      meter_carry_unknown_reasons: prevInfo?.unknownReasons?.[b.booth_code] ?? [],
    }
  })
  // SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_1: 入力画面と帳票で同一の共通比較関数を使う(順序定義を2箇所に書かない)。
  rows.sort(compareCollectionBooths)
  return { data: rows, error: null }
}

// SPEC-COLLECTION-PREV-METER-NEW-STORE-FALLBACK-01 (D-809): ブースごとの一番古い(in_meter が入っている)行。
//   patrol_date 昇順 → created_at 昇順の先頭。entry_type は問わない。in_meter が null の行は飛ばす。
//   ★ブースごとに limit(1) で引く。店全体を 1 本で引くと 1000 行の既定上限で古い行が黙って切れる(D-328/D-394 の轍)。
//   ★初回集金の店でしか走らない(確定集金がある店は従来経路)。
//   戻り: { map: { booth_code: in_meter }, minDate: 見つかった最古 patrol_date の最小値 | null }
export async function fetchFirstMeterByBooth(boothCodes) {
  const map = {}
  let minDate = null
  const results = await Promise.all((boothCodes ?? []).map(code =>
    supabase.from('meter_readings')
      .select('booth_code, in_meter, patrol_date, created_at')
      .eq('booth_code', code)
      .not('in_meter', 'is', null)
      .order('patrol_date', { ascending: true })
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => (error ? null : data))
      .catch(() => null),
  ))
  for (const r of results) {
    if (!r || r.in_meter == null) continue
    map[r.booth_code] = r.in_meter
    const d = String(r.patrol_date ?? '').slice(0, 10)
    if (d && (minDate == null || d < minDate)) minDate = d
  }
  return { map, minDate }
}

// J-COLLECTION-05 fix_E: prev_date 以降の最古 meter_readings.in_meter を booth単位で返す。
//   prev_date 未指定時は当該店舗の直前confirmed cash_collections.collected_at を自動採用。
//   meter_readings 0件なら 直前confirmed cash_collection_booths.in_meter_current にフォールバック。
export async function getPrevMeterAfterDate(storeCode, boothCodes, prevDate, collectedAt) {
  const empty = { prevMap: {}, compMap: {}, unknownSet: {} }
  if (!storeCode || !boothCodes || boothCodes.length === 0) return empty

  // 1) effective prev_date を決める
  let effectivePrev = prevDate || null
  let latestCol = null
  if (!effectivePrev) {
    const { data } = await supabase.from('cash_collections')
      .select('collection_id, collected_at')
      .eq('store_code', storeCode).eq('status', 'confirmed')
      .order('collected_at', { ascending: false }).limit(1).maybeSingle()
    if (data) { effectivePrev = data.collected_at; latestCol = data }
  }

  let map = {}
  // SPEC-COLLECTION-PREV-METER-NEW-STORE-FALLBACK-01 (D-809): 確定集金が 1 件も無い店(=初回集金)で
  //   prevDate も未指定のときだけ、ブースごとの一番古い meter_readings.in_meter を前回値にする。
  //   ★ひろ「設置時のスタートメーター入力してるのになんで手入力しなきゃいけないんだよ」。
  //   ★entry_type で絞らない(FKK01 の最古行は patrol。replace に絞ると取りこぼす / REJECTED 一)。
  //   ★確定集金がある店・prevDate 指定ありは下の従来経路のまま 1 バイトも変えない(REJECTED 二)。
  const isFirstCollection = !effectivePrev
  if (isFirstCollection) {
    // ★best-effort。失敗しても従来どおり空(=手入力)で集金入力は動く(D-583 の補償と同じ思想)。
    let first = { map: {}, minDate: null }
    try { first = await fetchFirstMeterByBooth(boothCodes) } catch (_e) { return empty }
    if (!first.minDate) return empty // 本当の新規店(meter_readings 0 件)は従来どおり空
    map = first.map
    // D-583 補償の下限: 見つかった最古日の最小値(ブース個別ではなく全体の min で包含的に拾う / AC3)
    effectivePrev = first.minDate
  }

  // 2) patrol_date >= effectivePrev の最古 in_meter を booth毎に取得
  if (!isFirstCollection) {
    // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
    const { data: mr } = await supabase.from('meter_readings')
      .select('booth_code, in_meter, patrol_date, created_at')
      .in('booth_code', boothCodes)
      .eq('entry_type', 'patrol')
      .gte('patrol_date', effectivePrev)
      .order('patrol_date', { ascending: true })
      .order('created_at', { ascending: true })
    for (const r of mr ?? []) {
      if (!(r.booth_code in map) && r.in_meter != null) map[r.booth_code] = r.in_meter
    }
  }

  // 3) fallback: meter_readings 0件のboothは confirmed cash_collection_booths.in_meter_current
  //   D-809: 初回集金では確定集金が無いので見に行かない(引いても空)。
  const remaining = isFirstCollection ? [] : boothCodes.filter(c => !(c in map))
  if (remaining.length > 0) {
    let colId = latestCol?.collection_id
    if (!colId) {
      const { data: c } = await supabase.from('cash_collections')
        .select('collection_id').eq('store_code', storeCode).eq('status', 'confirmed')
        .order('collected_at', { ascending: false }).limit(1).maybeSingle()
      colId = c?.collection_id
    }
    if (colId) {
      // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
      const { data: bts } = await supabase.from('cash_collection_booths')
        .select('booth_code, in_meter_current').eq('collection_id', colId).in('booth_code', remaining)
      for (const b of bts ?? []) {
        if (b.in_meter_current != null) map[b.booth_code] = b.in_meter_current
      }
    }
  }
  // SPEC-COLLECTION-METER-CARRY-COMPENSATION-V1 (D-583) ITEM_1: 集金期間中の一周/交換を引く。
  //   ★足し戻す量は「carry + 旧メーター最終値」。carry だけでは全然足りない(meterCarryCompensation.js 参照)。
  //   ★best-effort。失敗しても補正なし(=今までどおりの 2 点引き算)で集金入力は動く。
  let compMap = {}
  let unknownSet = {}
  let unknownReasons = {}
  try {
    let q = supabase.from('meter_readings')
      // SPEC-METER-EXCHANGE-REPORT-01 (D-994): 交換/一周は報告(meter_exchanges)だけで見る。メモ由来の旧列は読まない。
      //   その日のイベント行を決めるため entry_type / created_at / updated_at も取る(SQL と同じ規則)。
      //   D-1073: in_meter も取る(下がったと報告して下がっていない交換は足し戻さない = meterCarryCompensation.js)。
      .select('booth_code, patrol_date, meter_exchanges, entry_type, created_at, updated_at, in_meter')
      .in('booth_code', boothCodes)
      .not('meter_exchanges', 'is', null)
      .gte('patrol_date', effectivePrev)
    if (collectedAt) q = q.lte('patrol_date', collectedAt)
    const { data: ev, error: evErr } = await q
      .order('patrol_date', { ascending: true })
      .limit(1000)
    if (!evErr && Array.isArray(ev)) {
      const sum = sumCompensations(ev)
      compMap = sum.byBooth
      unknownSet = Object.fromEntries(sum.unknownBooths.map(c => [c, true]))
      unknownReasons = sum.unknownReasons ?? {}
    }
  } catch (_e) { /* 補正なしで続行(集金入力を止めない) */ }

  return { prevMap: map, compMap, unknownSet, unknownReasons }
}

// 前回集金日選択時: 当該店舗の指定日の集金から各ブースのprevメーターを取得
// D-1021: 確定(status='confirmed')だけを見る。同じ日の重複を取り消した行(created_at が新しい)を前回値にしない。
//   ★eq('confirmed') にしたのは同じ前回メーター経路の getPrevMeterAfterDate(上)と揃えるため(下書きも前回にしない)。
export async function getPrevCollectionMeters(storeCode, prevDate) {
  if (!storeCode || !prevDate) return { data: {}, error: null }
  const { data: col, error: e1 } = await supabase
    .from('cash_collections')
    .select('collection_id')
    .eq('store_code', storeCode)
    .eq('collected_at', prevDate)
    .eq('status', 'confirmed')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (e1) return { data: null, error: e1 }
  if (!col) return { data: {}, error: null } // 該当日の集金なし

  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data: bts, error: e2 } = await supabase
    .from('cash_collection_booths')
    .select('booth_code, in_meter_current, out_meter_current')
    .eq('collection_id', col.collection_id)
  if (e2) return { data: null, error: e2 }

  const map = {}
  for (const b of bts ?? []) {
    map[b.booth_code] = { in_meter_prev: b.in_meter_current, out_meter_prev: b.out_meter_current }
  }
  return { data: map, error: null }
}

// 確定保存 (status=confirmed)。advance_payment / prev_collection_date 対応。
// J-COLLECTION-05: collectionId をUI側で事前生成して渡せるように対応(レシートupload pathと一致させる)。
// J-COLLECTION-09: 弊社署名 Storage URL/path を payload に追加 (cash_collections.staff_signature_url/path に保存)。
// SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 空欄は null にする(0 で埋めない)。
//   ★枚数は整数、金額は小数もありうるので分ける。
const numOrNullInt = v => (v === '' || v == null ? null : (Number.isFinite(Number(v)) ? Math.trunc(Number(v)) : null))
const numOrNullAmount = v => (v === '' || v == null ? null : (Number.isFinite(Number(v)) ? Number(v) : null))

export async function saveCollection({
  storeCode, collectedAt, prevCollectionDate, collectedBy, collectedByName, booths, rowData, notes,
  collectionId: providedId,
  staffSignatureUrl: providedSigUrl, staffSignaturePath: providedSigPath,
  customerSignatureUrl: providedCustomerSigUrl,
  customerSignaturePath: providedCustomerSigPath,
  customerSignedAt: providedCustomerSignedAt,
  // SPEC-COLLECTION-ATOMIC-IDEMPOTENT-01 (D-267) ITEM_3: 端末生成の冪等キー。渡されなければ生成(再送安全の土台)。
  idempotencyKey: providedIdempotencyKey,
  // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 一括計数。
  //   ★渡さなければ 1 ビットも変わらない(既存の通常入力の回帰なし)。
  //   countingMode='bulk' のときだけ、店舗全体で数えた金種と 1 枚のレシート、
  //   および各ブースの直入力金額(rowData[booth].amount)を一緒に送る。
  countingMode = 'itemized',
  bulkCounts = null,
  bulkReceiptPhotoUrl = null,
  bulkReceiptPhotoPath = null,
}) {
  const isBulk = countingMode === 'bulk'
  const idempotencyKey = providedIdempotencyKey || crypto.randomUUID()
  let collectionId = providedId
  if (!collectionId) {
    const { count } = await supabase.from('cash_collections')
      .select('collection_id', { count: 'exact', head: true })
      .eq('store_code', storeCode).eq('collected_at', collectedAt)
    const seq = (count ?? 0) + 1
    collectionId = genCollectionId(storeCode, collectedAt, seq)
  }
  const now = new Date().toISOString()

  // SPEC-COLLECTION-ATOMIC-IDEMPOTENT-01 (D-267) ITEM_3: 親行の列。executed_at はトリガ管轄なので書かない(LANDMINE_2)。
  //   D-090: 表示名は collected_by_name、updated_by は純監査の staffId(表示名を流用しない)。
  const collection = {
    collection_id: collectionId,
    store_code: storeCode,
    collected_by: collectedBy || null,
    collected_by_name: collectedByName || null,
    collected_at: collectedAt,
    prev_collection_date: prevCollectionDate || null,
    status: 'confirmed',
    notes: notes || null,
    organization_id: CHANGE_ORG_ID,
    created_at: now,
    updated_at: now,
    updated_by: collectedBy || null,
    // J-COLLECTION-09 fix_1: 弊社担当者署名 Storage URL/Path
    staff_signature_url: providedSigUrl || null,
    staff_signature_path: providedSigPath || null,
    // COLLECTION-SIGNATURE-REDESIGN-01 R1: 先方サイン (確定時同時保存)
    customer_signature_url: providedCustomerSigUrl || null,
    customer_signature_path: providedCustomerSigPath || null,
    customer_signed_at: providedCustomerSignedAt || null,
    // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 数え方と、店舗全体で数えた金種・レシート。
    //   ★itemized のときは counting_mode 以外すべて null。0 で埋めない(D-492: 数えていないものを 0 にしない)。
    counting_mode: isBulk ? 'bulk' : 'itemized',
    bulk_bill_10000: isBulk ? numOrNullInt(bulkCounts?.bill_10000) : null,
    bulk_bill_5000:  isBulk ? numOrNullInt(bulkCounts?.bill_5000)  : null,
    bulk_bill_1000:  isBulk ? numOrNullInt(bulkCounts?.bill_1000)  : null,
    bulk_coin_500:   isBulk ? numOrNullInt(bulkCounts?.coin_500)   : null,
    bulk_coin_100:   isBulk ? numOrNullInt(bulkCounts?.coin_100)   : null,
    bulk_coin_50:    isBulk ? numOrNullInt(bulkCounts?.coin_50)    : null,
    bulk_receipt_photo_url:  isBulk ? (bulkReceiptPhotoUrl || null)  : null,
    bulk_receipt_photo_path: isBulk ? (bulkReceiptPhotoPath || null) : null,
    // SPEC-PROFIT-SHARE-SERVER-SNAPSHOT-V1 (D-487) IMPL_1: 分配 3 列はクライアントから送らない。
    //   焼き付けはサーバ(fn_save_collection)が子 INSERT の後に stores.partner_share_pct と
    //   ブース合計から行う。旧ビルドの端末には分配入力欄が無く 3 列が null のまま確定する事故が
    //   実在した(FKG01-20260724-01)。本番では同時に 8 種類のビルドが稼働している実測があり、
    //   「新しい UI 要素に値の入力を依存させる」構造そのものが原因だったため、送る経路を無くす。
  }

  // boothRows の組み立て(id 生成規則・Number 変換・空文字 null 化)は現行挙動をそのまま保つ(D-267 ITEM_3)。total は生成列なので入れない(LANDMINE_1)。
  const boothRows = booths.map(b => {
    const d = rowData[b.booth_code] || {}
    const c = d.counts || {}
    return {
      id: `${collectionId}-${b.booth_code}`,
      collection_id: collectionId,
      booth_code: b.booth_code,
      machine_code: b.machine_code,
      store_code: storeCode,
      bill_10000: Number(c.bill_10000) || 0,
      bill_5000: Number(c.bill_5000) || 0,
      bill_1000: Number(c.bill_1000) || 0,
      coin_500: Number(c.coin_500) || 0,
      coin_100: Number(c.coin_100) || 0,
      coin_50: Number(c.coin_50) || 0,
      in_meter_prev: d.in_meter_prev ?? null,
      in_meter_current: d.in_meter_current === '' || d.in_meter_current == null ? null : Number(d.in_meter_current),
      out_meter_prev: d.out_meter_prev ?? null,
      out_meter_current: d.out_meter_current === '' || d.out_meter_current == null ? null : Number(d.out_meter_current),
      advance_payment: Number(d.advance_payment) || 0,
      notes: d.notes && String(d.notes).trim() !== '' ? String(d.notes).trim() : null,
      receipt_photo_url: d.receipt_photo_url || null,
      receipt_photo_path: d.receipt_photo_path || null,
      created_at: now,
      // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 一括計数のときだけ、このブースの集金額を直接入れる。
      //   ★total は生成列で書けない(D-267 LANDMINE_1)。D-689 で
      //     total = coalesce(bulk_amount, 金種×単価) に作り替えてあるので、ここに入れれば
      //     total を読んでいる既存の全員(集計 / 分配 / 監査 / PDF / 照合)がそのまま正しくなる。
      //   ★itemized では必ず null。0 を入れると「金額 0 円の集金」として total を上書きしてしまう。
      bulk_amount: isBulk ? numOrNullAmount(d.amount) : null,
    }
  })
  // SPEC-COLLECTION-ATOMIC-IDEMPOTENT-01 (D-267) ITEM_2/3: 親+明細を単一トランザクションの RPC で原子的に書く。
  //   旧2本の直 insert を廃止。同じ idempotency_key の再送は RPC 側でリプレイ(新規行を作らず既存 collection_id を返す)。
  const { data: rpcData, error: rpcErr } = await supabase.rpc('fn_save_collection', {
    p_payload: { idempotency_key: idempotencyKey, collection, booths: boothRows },
  })
  if (rpcErr) return { data: null, error: rpcErr }
  // リプレイ時は既存行の collection_id が返る。以降の後処理は RPC が返した実 id を使う。
  const savedCollectionId = rpcData?.collection_id ?? collectionId

  // SPEC-COLLECTION-FLAG-SYNC-01 (D-253) ITEM_4: 保存成功後に集金フラグを整合(立てる+落とす)。RPC の外に残す(スコープ最小化)。
  //   AC4: フラグ処理が例外を投げても保存成功を打ち消さない(内部握り潰し + 呼出側 try/catch の二重防御)。
  try { await syncCollectionFlags(savedCollectionId) } catch { /* フラグ整合失敗は保存成功を打ち消さない */ }

  // SPEC-ANOMALY-METER-P1-COLLECTION-AUDIT-01 (D-267 ITEM_4): commit 後に fire-and-forget で監査。RPC の外に残す。
  // await しない (保存応答をブロックしない)。auditCollectionMeters は内部で例外を握り潰す。
  auditCollectionMeters(savedCollectionId)

  return { data: { collectionId: savedCollectionId }, error: null }
}

// SPEC-COLLECTION-INPUT-PERIOD-HEADER-01 (D-254) S2: 店舗選択時に前回/前々回の集金を軽量取得(クエリ1本)。
//   当該 store_code の cash_collections を collected_at 降順で limit 件(既定2件)。前回の集計期間を出すには
//   前々回の collected_at が要るため2件取る。collection_id / collected_at / executed_at のみ。
//   ブース一覧の重い取得(getActiveBoothsForStore)はここに乗せない(読み込むボタンのまま)。
//   RLS 厳守、anon の select に organization_id フィルタを付けない。
//   FIX_01 (gate_3): baselineDate を渡すと lt(collected_at, baselineDate)=「基準日より厳密に古い」だけを取る。
//     入力中の集金日を基準にすることで、後入力(唐津 7/13 を 7/30 に入れる等)でも入力日より新しい集金が前回として
//     出て期間が逆転するのを防ぐ。定義を D-252 getCollectionDetail の起算日(lt(collected_at, 当該))と一致させる。
//     baselineDate なし(null)の呼出は従来どおり全件から降順で取る(既存の呼び分けを壊さない)。
export async function getRecentCollections(storeCode, baselineDate = null, limit = 2) {
  if (!storeCode) return { data: [], error: null }
  // D-1021: 前回/前々回に取消(status='cancelled')の集金を出さない(getPrevCollectionMeters と同じ eq('confirmed'))。
  let query = supabase.from('cash_collections')
    .select('collection_id, collected_at, executed_at')
    .eq('store_code', storeCode)
    .eq('status', 'confirmed')
  if (baselineDate) query = query.lt('collected_at', baselineDate)
  const { data, error } = await query
    .order('collected_at', { ascending: false })
    .limit(limit)
  return { data: data ?? [], error }
}

// SPEC-COLLECTION-DUPLICATE-SUBMIT-GUARD-01 (D-803) AC2:
//   その店・その集金日に **既に確定済みの集金があるか** を 1 件だけ引く(読み取り専用)。
//
// ★使うのは「先方サイン」ボタンを押した瞬間だけ(AC3 / AC4)。
//   ★読み込んだ直後には出さない。集金入力そのものを止めない・早すぎる警告で現場を混乱させない(spec FORBIDDEN)。
// ★★ブロックするためではなく **人に判断させるため** の材料。
//   同じ店・同じ日に正当な理由で 2 件目を入れる運用(再訪・分割集金)は禁止されていないので、
//   UNIQUE 制約も時間窓ヒューズも入れない(spec REJECTED_ALTERNATIVES 一 / 二)。
// ★合計は cash_collection_booths をクライアントで足さず、既設の fn_collection_totals RPC を
//   **1 件分だけ** 呼ぶ(D-328 の 1000 行トラップを踏まない)。★新しい RPC は作らない。
// ★返り値は { data, error }。★引けなかったときは data=null で返し、呼び出し側は **そのまま先へ進む**
//   (fail-open / D-567 getCollectionSharePreview と同じ方針)。
//   ★お客様にサインをもらう直前に通信断で止まるのが一番まずい。
export async function getExistingConfirmedCollection(storeCode, collectedAt) {
  if (!storeCode || !collectedAt) return { data: null, error: null }
  try {
    const { data, error } = await supabase.from('cash_collections')
      .select('collection_id, store_code, collected_at, executed_at, collected_by_name')
      .eq('store_code', storeCode)
      .eq('collected_at', collectedAt)
      .eq('status', 'confirmed')
      .order('executed_at', { ascending: false })
      .limit(1)
    if (error) {
      Sentry.captureMessage?.('collection_duplicate_check_failed', {
        level: 'warning', extra: { store_code: storeCode, collected_at: collectedAt, raw: error.message },
      })
      return { data: null, error }
    }
    const row = (data ?? [])[0]
    if (!row) return { data: null, error: null }
    const { data: totalsRows } = await supabase.rpc('fn_collection_totals', { p_collection_ids: [row.collection_id] })
    const total = Number((totalsRows ?? [])[0]?.total ?? 0)
    return { data: { ...row, total }, error: null }
  } catch (e) {
    // ★通信断でもサインに進めること。確認を出せないことだけを諦める。
    Sentry.captureMessage?.('collection_duplicate_check_failed', {
      level: 'warning', extra: { store_code: storeCode, collected_at: collectedAt, raw: e?.message },
    })
    return { data: null, error: e }
  }
}

/**
 * SPEC-COLLECTION-DUPLICATE-SUBMIT-GUARD-01 (D-803) AC3 / AC4:
 *   二重登録の確認ダイアログの文言。★通常入力と一括集金で **同じ言葉** を使う。
 *   ★2 画面で文言を分岐させない(D-616: 同じ判断を 2 箇所に違う言葉で置かない)。
 *   ★「既に確定済み」という事実と、その中身(いつ・誰が・いくら)だけを出す。
 *     ★どうすべきかは書かない。決めるのは現場の人(ブロックではなく判断材料)。
 */
export function duplicateCollectionMessage(row) {
  const t = row?.executed_at
    ? new Date(row.executed_at).toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit' })
    : '時刻不明'
  const who = row?.collected_by_name || '担当者不明'
  const yen = Number(row?.total ?? 0).toLocaleString()
  return `この店舗・この集金日は既に確定済みです（${t} 実行・${who}・${yen}円）。続けて新しい集金として登録しますか?`
}

// 履歴一覧 (CollectionHistoryPage 用、変更なし)
export async function getCollectionHistory() {
  // SPEC-COLLECTION-EXECUTED-AT-01 (D-252) S2: 履歴に executed_at(サーバ打刻の実行日)を載せる。
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data: cols, error } = await supabase.from('cash_collections')
    .select('collection_id, store_code, collected_at, status, collected_by, signed_pdf_url, customer_signed_at, executed_at')
    .order('collected_at', { ascending: false })
  if (error) return { data: null, error }
  const ids = (cols ?? []).map(c => c.collection_id)
  let totals = {}
  let storeNames = {}
  if (ids.length) {
    // SPEC-COLLECTION-HISTORY-TOTAL-RPC-01 (D-328): cash_collection_booths を IN 一括取得して JS で SUM する経路は
    //   Supabase JS の1000行デフォルト上限に掛かり、超過分が黙って切り捨てられて表示額が過小になる(現物1045行で発生)。
    //   Postgres 側で GROUP BY 集計する fn_collection_totals RPC に置換=受信行数は集金件数のみで1000行トラップに掛からない。
    const { data: totalsRows } = await supabase.rpc('fn_collection_totals', { p_collection_ids: ids })
    for (const r of totalsRows ?? []) totals[r.collection_id] = Number(r.total || 0)
    const codes = [...new Set((cols ?? []).map(c => c.store_code))]
    // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
    const { data: stores } = await supabase.from('stores').select('store_code, store_name').in('store_code', codes)
    storeNames = Object.fromEntries((stores ?? []).map(s => [s.store_code, s.store_name]))
  }
  const rows = (cols ?? []).map(c => ({
    ...c,
    store_name: storeNames[c.store_code] || c.store_code,
    total: totals[c.collection_id] || 0,
  }))
  return { data: rows, error: null }
}

// SPEC-COLLECTION-HISTORY-KEYSET-01 (D-331): 集金履歴の keyset ページング(offset 不使用=ひろ承認方針)。
//   getCollectionHistory(全件無上限=D-330 是正保留)を置換する新経路。初回 limit=120、collected_at 降順 +
//   collection_id をタイブレイクキー(D-1088 で executed_at 降順に変更。下記)。cursor=null は先頭、cursor はその行より厳密に後ろ(古い方向)。
//   返り値は現行と同一形状(store_name/total 合成)+ nextCursor(最後の行のキー、件数が limit 未満=最終ページなら null)。
//   起算日(periodStart)の導出は呼び出し側(CollectionHistoryPage)の累積 rows + deriveHistoryPeriodStarts が担う(本関数は起算日を返さない)。
// D-1088: 並びと keyset を (executed_at, collection_id) の降順に変えた(登録した直後の集金が一覧の上に出る。
//   坂本さん報告: 10/18 に登録した今宿(回収日 10/13)が 13 日の位置にあった)。cursor = {executed_at, collection_id}。
//   ★期間の絞り込みは回収日(collected_at)のまま(合計・取消件数と同じ基準 / DECISION_2)。
//   ★executed_at は NOT NULL default now()(本番 232 行で NULL 0 件 / 2026-10-21 実測)なので NULL の並びは考えない。
//   ★値は二重引用符で包む(タイムスタンプの . と : は PostgREST の予約文字。storeTasks.js の keyset と同じ書き方)。
//   ★executed_at は文字列のまま次の cursor に渡す(Date にするとマイクロ秒が落ちて、同じ行を取り直したり飛ばしたりする)。
// D-1020: includeCancelled=false(既定)は取消(status='cancelled')を返さない。
//   坂本さん報告: 那珂川 9/11 が 4 件出た(3 件は D-802 で取消済み)。取消は管理者が「取消を表示」で見るときだけ返す。
//   ★サーバ側で絞る(keyset の cursor と件数がずれないように。クライアントで落とすとページが歯抜けになる)。
//   ★status は「cancelled 以外」で絞る(collectionBaseline D-961b と同じ書き方。将来 draft が出ても消さない)。
export async function getCollectionHistoryPage({ cursor = null, limit = 120, startDate = null, endDate = null, storeCodes = null, includeCancelled = false } = {}) {
  // D-395 ITEM_3: フィルタ(期間 JST/エリア/ブランド/店舗)は resolveStoreCodes 済の storeCodes と期間で受ける。
  //   storeCodes===null=店舗制約なし(従来=AC9)。storeCodes===[](制約はあるが該当ゼロ)は即空返し。
  if (Array.isArray(storeCodes) && storeCodes.length === 0) return { data: [], nextCursor: null, error: null }
  let q = supabase.from('cash_collections')
    .select('collection_id, store_code, collected_at, status, collected_by, signed_pdf_url, customer_signed_at, executed_at')
  if (!includeCancelled) q = q.neq('status', 'cancelled')
  if (startDate) q = q.gte('collected_at', startDate)          // 両端含む(collected_at は date 型=JST暦日)
  if (endDate) q = q.lte('collected_at', endDate)
  if (Array.isArray(storeCodes) && storeCodes.length > 0) q = q.in('store_code', storeCodes)
  if (cursor && cursor.executed_at != null && cursor.collection_id != null) {
    // keyset: (executed_at, collection_id) の降順で cursor より厳密に後ろ。offset は使わない。
    const quote = v => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
    const at = quote(cursor.executed_at)
    q = q.or(`executed_at.lt.${at},and(executed_at.eq.${at},collection_id.lt.${quote(cursor.collection_id)})`)
  }
  const { data: cols, error } = await q
    .order('executed_at', { ascending: false })
    .order('collection_id', { ascending: false })
    .limit(limit)
  if (error) return { data: null, nextCursor: null, error }
  const ids = (cols ?? []).map(c => c.collection_id)
  let totals = {}
  let storeNames = {}
  if (ids.length) {
    // D-328 と同型: ページ内 collection_id のみで fn_collection_totals(1000行トラップを避ける server 集計)。
    const { data: totalsRows } = await supabase.rpc('fn_collection_totals', { p_collection_ids: ids })
    for (const r of totalsRows ?? []) totals[r.collection_id] = Number(r.total || 0)
    const codes = [...new Set((cols ?? []).map(c => c.store_code))]
    // full-scan-intentional: 店名解決は in(ページ内 store codes) で件数上限あり(<=limit)。order/limit 不要。
    const { data: stores } = await supabase.from('stores').select('store_code, store_name').in('store_code', codes)
    storeNames = Object.fromEntries((stores ?? []).map(s => [s.store_code, s.store_name]))
  }
  const rows = (cols ?? []).map(c => ({
    ...c,
    store_name: storeNames[c.store_code] || c.store_code,
    total: totals[c.collection_id] || 0,
  }))
  const last = rows[rows.length - 1]
  // 件数が limit 未満 = 最終ページ → nextCursor=null(以降の追加取得を止める)。
  const nextCursor = (rows.length === limit && last)
    ? { executed_at: last.executed_at, collection_id: last.collection_id }
    : null
  return { data: rows, nextCursor, error: null }
}

// SPEC-COLLECTION-HISTORY-FILTER-AND-ROWCAP-FIX-01 (D-395) ITEM_3: フィルタ用の店舗メタ(エリア/ブランドは実値から動的に作る)。
// SPEC-COLLECTION-HISTORY-STORE-SELECT-01 (D-960): locality_kana を追加(店舗選択の並び順キー。
//   sortStoresJa がこれを使う。DB の order は候補生成の安定化用で、表示順は sortStoresJa が上書きする)。
export async function getStoresForFilter() {
  // eslint-disable-next-line supabase-guard/require-bound -- D-395 ITEM_3: 店舗マスタ(小・数十件)をフィルタ選択肢用に全件取得(絞り込みの候補生成につき上限を付けない)
  const { data, error } = await supabase.from('stores')
    .select('store_code, store_name, area, brand_name, locality_kana')
    .order('area', { ascending: true })
    .order('store_name', { ascending: true })
  return { data: data ?? [], error }
}

// D-1062: 店ごとの最新の実行日(取消を除いた max(executed_at))。集金履歴の店舗の並びに使う。
//   ★store_code と executed_at だけを 1000 行ずつ全件取り(fetchAllRange)、店ごとの最大は画面側の純関数で出す。
//   ★organization_id では絞らない(RLS で担保済み。anon で絞ると空になる)。
//   失敗したら data={}(並びが五十音順に戻るだけで、画面は止めない)。
export async function getLatestCollectionDateByStore() {
  // eslint-disable-next-line supabase-guard/require-bound -- D-1062: fetchAllRange が .range() で全件ページング(上限はページ制御)
  const buildQuery = () => supabase.from('cash_collections')
    .select('store_code, executed_at')
    .neq('status', 'cancelled')
    .order('collection_id', { ascending: true })
  try {
    const rows = await fetchAllRange(buildQuery, { pageSize: 1000 })
    return { data: latestExecutedAtByStore(rows), error: null }
  } catch (e) {
    return { data: {}, error: e }
  }
}

// SPEC-COLLECTION-HISTORY-FILTER-AND-ROWCAP-FIX-01 (D-395) ITEM_3_TOTAL: 絞り込み後の全集金の合計。
//   ★合計は必ず fn_collection_totals RPC の戻り値を足し合わせて求める(cash_collection_booths をクライアントで
//     取得・合算しない=ITEM_1 で直した不具合を作り直さない=本specの根幹)。
//   フィルタ後の collection_id を全件(ページング=fetchAllRange で1000行で切れない)集めて RPC に渡し合算。
//   storeCodes===[] は該当ゼロ→合計0。null は制約なし=全集金合計。
// D-1134: 第 2 引数 { shared }(呼び出し側が 1 回の画面読み込みごとに作る空のオブジェクト `{}`)を渡すと、
//   同じ shared・同じ期間の「id 取得(store_code つき)→ fn_collection_totals」を 1 回だけ行い、店の絞りは手元で行う。
//   ★fn_collection_totals は collection_id ごとの合計を返す(group by collection_id / 本番 pg_get_functiondef 実測)ので、
//     部分集合の和は「その店だけで呼んだ RPC の和」と一致する。★新しい RPC は作らない。
//   ★キャッシュは shared をキーにした WeakMap にだけ持つ(shared を捨てれば消える。集金を保存した後に
//     別の読み込みへ古い合計を返さない)。
//   売上予測(ForecastList)が全店・ブランド別・担当別で同じ月を何度も引いていたのを 1 往復ぶんにまとめるため。
const _sharedRanges = new WeakMap()

function _loadSharedRange(shared, startDate, endDate) {
  let byRange = _sharedRanges.get(shared)
  if (!byRange) { byRange = new Map(); _sharedRanges.set(shared, byRange) }
  const key = `${startDate ?? ''}|${endDate ?? ''}`
  let p = byRange.get(key)
  if (!p) {
    p = (async () => {
      const buildIdQuery = () => {
        // D-1020 と同じ絞り(取消を除く・回収日の期間)。店は絞らず store_code を一緒に取る。
        let q = supabase.from('cash_collections').select('collection_id, store_code')
          .neq('status', 'cancelled')
          .order('collection_id', { ascending: true })
        if (startDate) q = q.gte('collected_at', startDate)
        if (endDate) q = q.lte('collected_at', endDate)
        return q
      }
      let idRows
      try {
        idRows = await fetchAllRange(buildIdQuery, { pageSize: 1000 })
      } catch (e) {
        return { rows: null, totalById: null, error: e }
      }
      const ids = idRows.map(r => r.collection_id)
      if (!ids.length) return { rows: [], totalById: new Map(), error: null }
      const { data: totalsRows, error } = await supabase.rpc('fn_collection_totals', { p_collection_ids: ids })
      if (error) return { rows: null, totalById: null, error }
      const totalById = new Map((totalsRows ?? []).map(r => [r.collection_id, Number(r.total || 0)]))
      return { rows: idRows, totalById, error: null }
    })()
    byRange.set(key, p)
  }
  return p
}

export async function getCollectionsTotalForFilters({ startDate = null, endDate = null, storeCodes = null } = {}, { shared = null } = {}) {
  if (Array.isArray(storeCodes) && storeCodes.length === 0) return { total: 0, error: null }
  if (shared) {
    const got = await _loadSharedRange(shared, startDate, endDate)
    if (got.error) return { total: 0, error: got.error }
    const only = Array.isArray(storeCodes) ? new Set(storeCodes) : null
    let total = 0
    for (const r of got.rows) {
      if (only && !only.has(r.store_code)) continue
      total += got.totalById.get(r.collection_id) ?? 0
    }
    return { total, error: null }
  }
  const buildIdQuery = () => {
    // D-1020: 合計に取消(status='cancelled')を足さない。★履歴の「取消を表示」に関係なく常に除く。
    //   以前は status を見ておらず、ForecastList の月別集金実績も取消分だけ過大だった(本番実測 8 月 +2,242,800 / 9 月 +5,191,300)。
    let q = supabase.from('cash_collections').select('collection_id')
      .neq('status', 'cancelled')
      .order('collection_id', { ascending: true })
    if (startDate) q = q.gte('collected_at', startDate)
    if (endDate) q = q.lte('collected_at', endDate)
    if (Array.isArray(storeCodes) && storeCodes.length > 0) q = q.in('store_code', storeCodes)
    return q
  }
  let idRows
  try {
    idRows = await fetchAllRange(buildIdQuery, { pageSize: 1000 })
  } catch (e) {
    return { total: 0, error: e }
  }
  const ids = idRows.map(r => r.collection_id)
  if (!ids.length) return { total: 0, error: null }
  const { data: totalsRows, error } = await supabase.rpc('fn_collection_totals', { p_collection_ids: ids })
  if (error) return { total: 0, error }
  const total = (totalsRows ?? []).reduce((s, r) => s + Number(r.total || 0), 0)
  return { total, error: null }
}

// D-1020: 絞り込み(期間/店舗)に当たる取消(status='cancelled')の件数。集金履歴の「取消を表示 (n)」に使う。
//   ★件数だけ(head + count)。行は取らない。失敗したら count=null(ボタンの数字を出さないだけで、一覧は止めない)。
export async function getCancelledCountForFilters({ startDate = null, endDate = null, storeCodes = null } = {}) {
  if (Array.isArray(storeCodes) && storeCodes.length === 0) return { count: 0, error: null }
  try {
    let q = supabase.from('cash_collections')
      .select('collection_id', { count: 'exact', head: true })
      .eq('status', 'cancelled')
    if (startDate) q = q.gte('collected_at', startDate)
    if (endDate) q = q.lte('collected_at', endDate)
    if (Array.isArray(storeCodes) && storeCodes.length > 0) q = q.in('store_code', storeCodes)
    const { count, error } = await q
    if (error) return { count: null, error }
    return { count: count ?? 0, error: null }
  } catch (e) {
    return { count: null, error: e }
  }
}

// SPEC-BILLING-SEAL-STORAGE-MIGRATE-01 (D-201a) S4/AC5: 角印の取得源を解決する。billing-assets の
//   seal_image_path から署名付きURLを render 時に発行し、画像を dataURL 化して返す。バイトはモデルを経由しない。
//   seal_image_path が null / 署名URL生成失敗 / 画像fetch失敗 のいずれも角印なし(null)にフォールバックし、
//   PDF生成を絶対に止めない。client は認証済 supabase(サービスキー不使用、RLS/バケットポリシーは変更しない)。
export async function resolveSealDataUrl(client, sealImagePath) {
  if (!sealImagePath) return null
  try {
    const { data, error } = await client.storage
      .from('billing-assets')
      .createSignedUrl(sealImagePath, 60)
    if (error || !data?.signedUrl) return null
    const res = await fetch(data.signedUrl)
    if (!res.ok) return null
    const bytes = new Uint8Array(await res.arrayBuffer())
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    const b64 = (typeof btoa !== 'undefined')
      ? btoa(bin)
      : (typeof Buffer !== 'undefined' ? Buffer.from(bin, 'binary').toString('base64') : null)
    return b64 ? `data:image/png;base64,${b64}` : null
  } catch {
    return null
  }
}

// PDF再表示用 詳細 (advance_payment / prev_collection_date 込み)
export async function getCollectionDetail(collectionId) {
  const { data: col, error } = await supabase.from('cash_collections')
    .select('*').eq('collection_id', collectionId).single()
  if (error) return { data: null, error }
  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data: booths, error: e2 } = await supabase.from('cash_collection_booths')
    .select('*').eq('collection_id', collectionId)
  if (e2) return { data: null, error: e2 }

  // SPEC-COLLECTION-EXECUTED-AT-01 (D-252) S2: 起算日(periodStart)= 同一 store_code で collected_at が
  //   当該集金より厳密に小さい行のうち collected_at 最大。該当なし(初回)は null。RLS 厳守、anon に organization_id フィルタを付けない。
  const { data: prevCol } = await supabase.from('cash_collections')
    .select('collected_at')
    .eq('store_code', col.store_code)
    .lt('collected_at', col.collected_at)
    .order('collected_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  const periodStart = prevCol?.collected_at ?? null

  // J-COLLECTION-13: 発行元を DB から resolve するため store.billing_entity_id を併取得
  const { data: store } = await supabase.from('stores')
    .select('store_name, store_name_official, billing_entity_id').eq('store_code', col.store_code).single()
  // 発行元 entity (company_name は NOT NULL、zip/address/tel/seal_image_path は NULL 許容)。
  // billing_entity_id が NULL の店舗は issuer なし扱い (fallback: PDF 側で company_name 行 skip まで含めて空ヘッダ)。
  // RLS 厳守、anon フィルタに organization_id を付けない (spec forbidden)。
  let issuer = null
  if (store?.billing_entity_id) {
    // SPEC-BILLING-SEAL-STORAGE-MIGRATE-01 (D-201a) S4: 角印は billing-assets の署名付きURL経由で取得する。
    //   D-183 の bytea DB列経路は廃止(bytea をモデル出力で通す破損リスクを断つ)。seal_image_path のみ SELECT。
    //   RLS 厳守で認証済 client の通常 SELECT のみ(organization_id フィルタは付けない)。
    const { data: ent } = await supabase.from('billing_entities')
      .select('id, company_name, zip, address, tel, seal_image_path')
      .eq('id', store.billing_entity_id)
      .maybeSingle()
    if (ent) ent.sealDataUrl = await resolveSealDataUrl(supabase, ent.seal_image_path)
    issuer = ent ?? null
  }
  const codes = [...new Set((booths ?? []).map(b => b.machine_code))]
  // SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_2: 並び替え材料の billing_order を SELECT に追加(他列は不変)。
  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data: machineData } = await supabase.from('machines')
    .select('machine_code, machine_name, machine_number, billing_order').in('machine_code', codes.length ? codes : ['__none__'])
  const mMap = Object.fromEntries((machineData ?? []).map(m => [m.machine_code, m]))

  // ブース表示名 (booth_label / booth_number) 補完
  const boothCodes = (booths ?? []).map(b => b.booth_code)
  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data: boothMaster } = await supabase.from('booths')
    .select('booth_code, booth_label, booth_number').in('booth_code', boothCodes.length ? boothCodes : ['__none__'])
  const bName = Object.fromEntries((boothMaster ?? []).map(b => [
    b.booth_code,
    b.booth_label || (b.booth_number != null ? `B${String(b.booth_number).padStart(2, '0')}` : b.booth_code),
  ]))

  const boothRows = (booths ?? []).map(b => {
    const m = mMap[b.machine_code] || {}
    return {
      ...b,
      machine_name: m.machine_name || b.machine_code,
      booth_name: bName[b.booth_code] || b.booth_code,
      // J-COLLECTION-03: rental_code = machine_number ?? machine_code 末尾セグメント
      rental_code: m.machine_number || (b.machine_code?.split('-').pop() ?? b.machine_code),
      // SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_3: 並び替え材料。DB が返す物理順に依存させず表示時に順序を決める。
      billing_order: m.billing_order ?? null,
    }
  })
  // SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_3: 入力画面と同一の共通比較関数で並べてから返す。
  //   total/advanceTotal は順序非依存なので値は変わらない(AC3)。レシートページも booths を filter するため自動的に揃う(ITEM_4/AC4)。
  boothRows.sort(compareCollectionBooths)
  const total = boothRows.reduce((s, b) => s + Number(b.total || 0), 0)
  const advanceTotal = boothRows.reduce((s, b) => s + Number(b.advance_payment || 0), 0)
  return {
    // D-252 S2: periodStart(起算日、raw collected_at | null)を PDF(S4)へ供給。executed_at は collection にそのまま載る(select('*'))。
    data: { collection: col, store: store ?? {}, booths: boothRows, total, advanceTotal, issuer, periodStart },
    error: null,
  }
}

// SPEC-COLLECTION-PAST-EDIT-ADVANCE-01 (D-086): 過去集金編集用、店舗別の集金履歴 (collected_at 降順)。
// RLS 厳守、anon フィルタに organization_id を付けない (spec forbidden)。
// D-1020: includeCancelled=false(既定)は取消(status='cancelled')を返さない。
export async function getCollectionHistoryByStore(storeCode, { includeCancelled = false } = {}) {
  if (!storeCode) return { data: [], error: null }
  let q = supabase.from('cash_collections')
    .select('collection_id, store_code, collected_at, status, collected_by')
    .eq('store_code', storeCode)
  if (!includeCancelled) q = q.neq('status', 'cancelled')
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data: cols, error } = await q
    .order('collected_at', { ascending: false })
  if (error) return { data: null, error }
  const ids = (cols ?? []).map(c => c.collection_id)
  const totals = {}
  if (ids.length) {
    // SPEC-COLLECTION-HISTORY-BY-STORE-TOTAL-RPC-01 (D-329): getCollectionHistory(D-328)と同型の1000行トラップを先潰し。
    //   store_code で絞るため顕在化前だが、佐賀45ブース×月1集金なら約2年で1000行超過→店舗別履歴額が黙って過小になる。
    //   既設の fn_collection_totals(D-328)を再利用(RPC の新規/変更なし)。受信行数=集金件数のみで上限に掛からない。
    const { data: totalsRows } = await supabase.rpc('fn_collection_totals', { p_collection_ids: ids })
    for (const r of totalsRows ?? []) totals[r.collection_id] = Number(r.total || 0)
  }
  return { data: (cols ?? []).map(c => ({ ...c, total: totals[c.collection_id] || 0 })), error: null }
}

// SPEC-COLLECTION-PAST-EDIT-ADVANCE-01 (D-086): 立替金 (advance_payment) と備考 (notes) "のみ" を更新する。
// メーター/集金額/金種/署名は署名済確定値のため一切触らない (update 列を advance_payment/notes に限定)。
// 立替は合計除外の参考値のため事後編集で署名済合計と矛盾しない。親 cash_collections の updated_at/updated_by を
// 監査として更新する (cash_collection_booths に updated_* 列は無いため親側に記録)。
export async function updateCollectionAdvanceNotes(collectionId, boothUpdates, staffId) {
  if (!collectionId) return { error: new Error('updateCollectionAdvanceNotes: missing collectionId') }
  for (const u of boothUpdates ?? []) {
    // 更新列は advance_payment / notes のみ (他列の更新経路を作らない = AC3)
    const payload = {
      advance_payment: (u.advance_payment === '' || u.advance_payment == null) ? 0 : Number(u.advance_payment),
      notes: (u.notes && String(u.notes).trim()) ? String(u.notes).trim() : null,
    }
    const { error } = await supabase.from('cash_collection_booths').update(payload).eq('id', u.id)
    if (error) return { error }
  }
  const { error: pErr } = await supabase.from('cash_collections')
    .update({ updated_at: new Date().toISOString(), updated_by: staffId ?? null })
    .eq('collection_id', collectionId)
  return { error: pErr ?? null }
}

/**
 * SPEC-COLLECTION-PAST-EDIT-RECEIPT-V1 (D-762): 後から機械行にレシート写真を付ける。
 *
 * ★D-086 の updateCollectionAdvanceNotes は「更新列は advance_payment / notes のみ
 *   (他列の更新経路を作らない = AC3)」と **意図して狭くしてある**。だから広げずに別で足す。
 *   ★既存の立替後入力の挙動を 1 バイトも変えない(本 D の FORBIDDEN)。
 * ★更新するのは receipt_photo_url / receipt_photo_path の 2 列だけ。
 * ★confirmed / 署名済みでも止めない。立替の後入力と同じ扱いにする
 *   (あちらもチェックしていない。写真だけ厳しくも緩くもしない)。
 * ★親の updated_at / updated_by を touch するのも立替と同じ。
 *
 * @param {string} collectionId
 * @param {Array<{id:string, receipt_photo_url:string, receipt_photo_path:string}>} receiptUpdates
 * @param {string|null} staffId
 */
export async function updateCollectionBoothReceipts(collectionId, receiptUpdates, staffId) {
  if (!collectionId) return { error: new Error('updateCollectionBoothReceipts: missing collectionId') }
  const list = receiptUpdates ?? []
  if (list.length === 0) return { error: null }
  for (const u of list) {
    if (!u?.id) return { error: new Error('updateCollectionBoothReceipts: missing booth id') }
    const { error } = await supabase.from('cash_collection_booths').update({
      receipt_photo_url: u.receipt_photo_url ?? null,
      receipt_photo_path: u.receipt_photo_path ?? null,
    }).eq('id', u.id)
    if (error) return { error }
  }
  const { error: pErr } = await supabase.from('cash_collections')
    .update({ updated_at: new Date().toISOString(), updated_by: staffId ?? null })
    .eq('collection_id', collectionId)
  return { error: pErr ?? null }
}

// J-COLLECTION-05: 撮影画像を Storage 'receipts' バケットにupload。upsertで再撮影上書き可。
//   path: '{org_id}/{collection_id}/{booth_code}.jpg'
//   返り値: { data:{path,url}, error }
export async function uploadReceiptPhoto({ collectionId, boothCode, fileBlob }) {
  if (!collectionId || !boothCode || !fileBlob) {
    return { data: null, error: new Error('uploadReceiptPhoto: missing args') }
  }
  const path = `${DFX_ORG_ID}/${collectionId}/${boothCode}.jpg`
  const { error: upErr } = await supabase.storage.from('receipts')
    .upload(path, fileBlob, { upsert: true, contentType: 'image/jpeg' })
  if (upErr) return { data: null, error: upErr }
  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)
  return { data: { path, url: publicUrl }, error: null }
}

/**
 * SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 一括計数の店舗レシート(1 枚)。
 *   ★ブース別レシートと同じ receipts バケットに置くが、名前で区別できるようにする。
 *     ブース別は {org}/{collection_id}/{booth_code}.jpg なので、店舗全体は _store を使う。
 *     ★ブースコードは店舗コード + 連番(KKY01-01 等)で先頭に _ が付かないため衝突しない。
 */
export async function uploadBulkReceiptPhoto({ collectionId, fileBlob }) {
  if (!collectionId || !fileBlob) {
    return { data: null, error: new Error('uploadBulkReceiptPhoto: missing args') }
  }
  const path = `${DFX_ORG_ID}/${collectionId}/_store.jpg`
  const { error: upErr } = await supabase.storage.from('receipts')
    .upload(path, fileBlob, { upsert: true, contentType: 'image/jpeg' })
  if (upErr) return { data: null, error: upErr }
  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)
  return { data: { path, url: publicUrl }, error: null }
}

// J-COLLECTION-06: 先方署名済PDFを Storage に upsert + cash_collections の signed_pdf_* / customer_signed_at を UPDATE。
//   path: '{org_id}/{collection_id}/signed.pdf'
// J-COLLECTION-11: 任意で customer_signature_url / customer_signature_path も同 UPDATE で書き込む
//                  (先方署名の永続化、後続 PDF 再生成で署名画像を embed 可能に)。
//   返り値: { data:{path,url}, error }
export async function saveSignedPdf({
  collectionId, fileBlob,
  customerSigUrl: providedCustomerSigUrl,
  customerSigPath: providedCustomerSigPath,
}) {
  if (!collectionId || !fileBlob) {
    return { data: null, error: new Error('saveSignedPdf: missing args') }
  }
  const path = `${DFX_ORG_ID}/${collectionId}/signed.pdf`
  const { error: upErr } = await supabase.storage.from('receipts')
    .upload(path, fileBlob, { upsert: true, contentType: 'application/pdf' })
  if (upErr) return { data: null, error: upErr }
  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)
  const updatePayload = {
    signed_pdf_url: publicUrl,
    signed_pdf_path: path,
    customer_signed_at: new Date().toISOString(),
  }
  if (providedCustomerSigUrl) updatePayload.customer_signature_url = providedCustomerSigUrl
  if (providedCustomerSigPath) updatePayload.customer_signature_path = providedCustomerSigPath
  const { error: updErr } = await supabase.from('cash_collections')
    .update(updatePayload)
    .eq('collection_id', collectionId)
  if (updErr) return { data: null, error: updErr }

  // SPEC-COLLECTION-FLAG-SYNC-01 (D-253): 先方署名確定でも集金フラグを整合させる。旧インライン実装(店舗一括で
  //   「立てる」だけ・落とさない)を共通関数 syncCollectionFlags へ置換=実装を二重に持たない(AC1)。
  //   これで対象がブース単位に限定され(AC2)、集金日より後に残ったフラグを落とす処理も通る(DESIGN_KEY_POINT)。
  //   失敗しても署名確定の主成功を打ち消さない(内部で warn 握り潰し + 保険の try/catch)。
  try { await syncCollectionFlags(collectionId) } catch { /* フラグ整合失敗は署名確定を打ち消さない */ }

  return { data: { path, url: publicUrl }, error: null }
}

// J-COLLECTION-09: 担当者署名(dataURL)を Storage 'receipts' へ PNG として upsert する内部 helper。
// J-COLLECTION-11: 先方署名も同じ経路を再利用するため _uploadSignature に抽出し、
//                  uploadStaffSignature / uploadCustomerSignature は薄い wrapper。
async function _uploadSignature({ collectionId, dataUrl, fileName }) {
  if (!collectionId || !dataUrl || !fileName) {
    return { data: null, error: new Error('_uploadSignature: missing args') }
  }
  // dataURL → Blob (画像圧縮は行わない、署名線が劣化するため raw PNG のまま保存)
  let blob
  try {
    const res = await fetch(dataUrl)
    blob = await res.blob()
  } catch (e) {
    return { data: null, error: e }
  }
  const path = `${DFX_ORG_ID}/${collectionId}/${fileName}`
  const { error: upErr } = await supabase.storage.from('receipts')
    .upload(path, blob, { upsert: true, contentType: 'image/png' })
  if (upErr) return { data: null, error: upErr }
  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)
  return { data: { path, url: publicUrl }, error: null }
}

// 弊社担当者署名 upload (path: '{org_id}/{collection_id}/staff_sig.png')
export async function uploadStaffSignature({ collectionId, dataUrl }) {
  return _uploadSignature({ collectionId, dataUrl, fileName: 'staff_sig.png' })
}

// J-COLLECTION-11: 先方担当者署名 upload (path: '{org_id}/{collection_id}/customer_sig.png')
// spec forbidden: 別経路新設禁止 → 内部は staff と同一の _uploadSignature を再利用。
export async function uploadCustomerSignature({ collectionId, dataUrl }) {
  return _uploadSignature({ collectionId, dataUrl, fileName: 'customer_sig.png' })
}

// J-COLLECTION-09 fix_4: レシート写真を Storage から削除。
//   path 必須 (cash_collection_booths.receipt_photo_path の値)。
//   返り値: { error } のみ。
export async function deleteReceiptPhoto({ path }) {
  if (!path) return { error: new Error('deleteReceiptPhoto: missing path') }
  const { error } = await supabase.storage.from('receipts').remove([path])
  return { error }
}

// J-COLLECTION-05: 保存前にcollectionIdを先取り(レシートupload pathに必要)
export async function nextCollectionId(storeCode, collectedAt) {
  const { count } = await supabase.from('cash_collections')
    .select('collection_id', { count: 'exact', head: true })
    .eq('store_code', storeCode).eq('collected_at', collectedAt)
  return genCollectionId(storeCode, collectedAt, (count ?? 0) + 1)
}

export { boothTotal }

// SPEC-COLLECTION-PDF-BULK-V1 (D-453) ITEM_2: 一括PDFの対象(読み取り専用)。
//   月(YYYY-MM)+ 名義(all/donki/other)で confirmed の集金を集め、店名と写真総枚数を返す。
//   判定は stores.store_type==='donki'(ナイスランド=ドンキ)。テスト店(is_test_store)は除外。cash_collections への書込なし。
function _bulkMonthRange(yyyyMM) {
  const [y, m] = String(yyyyMM).split('-').map(Number)
  const start = `${yyyyMM}-01`
  const nm = m === 12 ? 1 : m + 1
  const ny = m === 12 ? y + 1 : y
  const end = `${ny}-${String(nm).padStart(2, '0')}-01`
  return { start, end }
}

export async function getBulkPdfCollections({ month, meiki = 'donki' }) {
  if (!month) return { rows: [], photoCount: 0, error: null }
  const { start, end } = _bulkMonthRange(month)
  // D-455 ITEM_6.1: limit(1000) を撤去。D-395 が閉じた「1000行で黙って切れる」罠を持ち込まないよう
  //   全件ページング(fetchAllRange)+ 期待件数(exact count)突合で全件取得を保証する。
  // eslint-disable-next-line supabase-guard/require-bound -- D-455 ITEM_6.1: fetchAllRange が .range() で全件ページング(上限はページ制御)
  const buildQuery = () => supabase
    .from('cash_collections')
    .select('collection_id, store_code, collected_at, stores!inner(store_name, store_type, is_test_store)')
    .eq('status', 'confirmed')
    .gte('collected_at', start)
    .lt('collected_at', end)
    .order('collection_id', { ascending: true }) // 一意キーで安定ページング
  const countRes = await supabase
    .from('cash_collections')
    .select('collection_id', { count: 'exact', head: true })
    .eq('status', 'confirmed')
    .gte('collected_at', start)
    .lt('collected_at', end)
  if (countRes.error) return { rows: [], photoCount: 0, error: countRes.error }
  let data
  try {
    data = await fetchAllRange(buildQuery, { pageSize: 1000, expectedCount: countRes.count ?? 0 })
  } catch (e) {
    return { rows: [], photoCount: 0, error: e }
  }
  const rows = (data ?? [])
    .filter(r => r.stores && r.stores.is_test_store !== true) // テスト店除外
    .filter(r => {
      const isDonki = r.stores.store_type === 'donki'
      if (meiki === 'donki') return isDonki
      if (meiki === 'other') return !isDonki
      return true // all
    })
    .map(r => ({ collection_id: r.collection_id, store_code: r.store_code, store_name: r.stores.store_name ?? r.store_code }))

  // 写真総枚数(receipt_photo_url ありのブース数)。collection_id を 200件ずつ。
  let photoCount = 0
  const ids = rows.map(r => r.collection_id)
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200)
    const { count } = await supabase
      .from('cash_collection_booths')
      .select('id', { count: 'exact', head: true })
      .in('collection_id', chunk)
      .not('receipt_photo_url', 'is', null)
    photoCount += count ?? 0
  }
  return { rows, photoCount, error: null }
}

/**
 * SPEC-COLLECTION-SHARE-PREVIEW-V1 (D-567) ITEM_3/ITEM_4:
 *   サインをもらう前に、按分額と本日のお渡し額をお客様の目の前で出すための読み取り専用 RPC。
 *
 * ★JS で計算しない。D-487 で computeProfitShare を削除してサーバ 1 箇所に寄せたのを復活させない。
 *   式は fn_profit_share_calc にあり、確定保存(fn_save_collection)と同じ関数を通る。
 *   だからサイン前に見せた数字と、確定後に伝票へ焼き付く数字が構造的に一致する。
 *
 * ★呼び出し側は失敗をサインの妨げにしないこと。集金の現場は電波が悪く、
 *   ここで止めると現場が進めなくなる方が害が大きい(ITEM_4)。
 *   失敗時は null を返すだけにして、例外を投げない。
 *
 * @returns {Promise<null | {
 *   store_code: string, total_sum: number, advance_sum: number,
 *   partner_share_pct: number|null, collection_method: string|null,
 *   base: number|null, customer_amount: number|null,
 *   company_amount: number|null, payout_today: number|null }>}
 */
export async function getCollectionSharePreview(storeCode, totalSum, advanceSum) {
  try {
    const { data, error } = await supabase.rpc('fn_collection_share_preview', {
      p_store_code: storeCode,
      p_total:      totalSum,
      p_advance:    advanceSum,
    })
    if (error) {
      Sentry.captureMessage?.('collection_share_preview_failed', {
        level: 'warning', extra: { store_code: storeCode, raw: error.message },
      })
      return null
    }
    return data ?? null
  } catch (e) {
    // ★通信断でもサインに進めること。表示だけ諦める。
    Sentry.captureMessage?.('collection_share_preview_failed', {
      level: 'warning', extra: { store_code: storeCode, raw: e?.message },
    })
    return null
  }
}
